import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { App, type LetterCopy, type PurchaseSlot } from "../App";
import { COUNTRIES } from "./countries";
import {
  CHINA_MIRROR,
  activationLink,
  alipayCheckoutUrl,
  fetchOrderStatus,
  openCheckout,
  preloadCheckouts,
  providerFor,
  onFastSpringOrder,
  returnedOrderProof,
  withoutReturnParams,
  type OrderProof,
  type OrderStatus,
} from "./checkout";
import "./purchase.css";

/** The China copy sells in CNY through 2Checkout's Alipay checkout. */
const PRICE = CHINA_MIRROR ? "¥89" : "$14.99";
/** Pending this long after payment, the page explains the delay. */
const PROCESSING_AFTER = 15_000;
/** Remembered so a reload (or a later visit) still shows the order. */
const ORDER_STORAGE_KEY = "typora-v2-purchase";
const ORDER_LIFETIME = 7 * 24 * 60 * 60 * 1000;
/** Searched besides the English names. */
const ALIASES: Record<string, string[]> = {
  GB: ["England", "Britain", "UK"],
  US: ["USA", "America"],
  CN: ["中国", "Mainland China"],
};

/** The country of a 2Checkout checkout, kept across its return redirect. */
const CHECKOUT_COUNTRY_KEY = "typora-v2-purchase-country";

type Order = {
  /** 2Checkout: polled at the store with this proof. FastSpring: its order id. */
  payment: OrderProof | { provider: "fastspring"; id: string };
  /** FastSpring: what its `order.completed` browser script forwarded. */
  ready?: { email: string; licenses: string[] };
  country: string;
  paidAt: number;
};

function readOrder(): Order | null {
  try {
    const order = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) ?? "null") as Order | null;
    if (order?.payment?.provider && Date.now() - order.paidAt < ORDER_LIFETIME) return order;
  } catch {
    /* no storage, or nothing usable in it */
  }
  return null;
}

/** This load is 2Checkout's return redirect after payment. Read once, before the URL is cleaned. */
const RETURNED_ORDER: Order | null = (() => {
  const proof = returnedOrderProof(window.location.search);
  if (!proof) return null;
  let country = "";
  try {
    country = sessionStorage.getItem(CHECKOUT_COUNTRY_KEY) ?? "";
  } catch {
    /* unknown country: no China dialog */
  }
  return { payment: proof, country, paidAt: Date.now() };
})();

function storeOrder(order: Order | null) {
  try {
    if (order) localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));
    else localStorage.removeItem(ORDER_STORAGE_KEY);
  } catch {
    /* the order still shows until the page closes */
  }
}

/** The region in the browser's language (`zh-CN` → CN), else the US. */
function guessCountry(): string {
  for (const language of navigator.languages ?? [navigator.language]) {
    const region = /^[a-z]{2,3}(?:-[A-Za-z]{4})?-([A-Z]{2})\b/i.exec(language)?.[1]?.toUpperCase();
    if (region && COUNTRIES.some(([code]) => code === region)) return region;
  }
  return "US";
}

function countryName(code: string): string {
  return COUNTRIES.find(([value]) => value === code)?.[1] ?? code;
}

function matches(name: string, key: string): boolean {
  const lower = name.toLowerCase();
  return lower.startsWith(key) || lower.includes(` ${key}`);
}

// MARK: - Country picker

type CountryDialogProps = {
  initial: string;
  busy: boolean;
  error: string;
  onCancel(): void;
  onContinue(country: string): void;
};

function CountryDialog({ initial, busy, error, onCancel, onContinue }: CountryDialogProps) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(initial);
  const listRef = useRef<HTMLUListElement>(null);
  const results = useMemo(() => {
    const key = query.trim().toLowerCase();
    if (!key) return COUNTRIES;
    return COUNTRIES.filter(
      ([code, name]) => matches(name, key) || code.toLowerCase() === key || ALIASES[code]?.some((a) => matches(a, key)),
    );
  }, [query]);

  // Keep the selection in view as it moves.
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [selected, results]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!busy) onCancel();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
    event.preventDefault();
    if (event.key === "Enter") {
      const target = results.some(([code]) => code === selected) ? selected : results[0]?.[0];
      if (target && !busy) onContinue(target);
      return;
    }
    if (results.length === 0) return;
    const index = results.findIndex(([code]) => code === selected);
    const next =
      index < 0 ? 0 : (index + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length;
    setSelected(results[next]![0]);
  };

  return (
    <div className="pc-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !busy && onCancel()}>
      <div className="pc-dialog" role="dialog" aria-modal="true" aria-labelledby="pc-country-title" onKeyDown={onKeyDown}>
        <h2 id="pc-country-title" className="pc-dialog-title">
          Select Country / Region
        </h2>
        <p className="pc-dialog-text">This sets the currency and taxes at checkout.</p>
        <input
          className="pc-search"
          type="search"
          placeholder="Search country or region…"
          aria-label="Search country or region"
          aria-controls="pc-country-list"
          autoComplete="off"
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            const key = event.target.value.trim().toLowerCase();
            const first = COUNTRIES.find(([, name]) => key && matches(name, key));
            if (first) setSelected(first[0]);
          }}
        />
        <ul id="pc-country-list" ref={listRef} className="pc-country-list" role="listbox" aria-label="Country or region">
          {results.map(([code, name]) => (
            <li
              key={code}
              role="option"
              aria-selected={code === selected}
              className="pc-country"
              onClick={() => setSelected(code)}
              onDoubleClick={() => !busy && onContinue(code)}
            >
              {name}
            </li>
          ))}
          {results.length === 0 ? <li className="pc-country-empty">No matches</li> : null}
        </ul>
        {error ? (
          <p className="pc-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="pc-dialog-actions">
          <button type="button" className="pc-text-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="submit-btn" onClick={() => onContinue(selected)} disabled={busy}>
            {busy ? "Opening checkout…" : `Continue with ${countryName(selected)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// MARK: - China server dialog

type ChinaDialogProps = { status: OrderStatus | null; onClose(): void };

/** Buyers in China: the default activation server may be out of reach. */
function ChinaServerDialog({ status, onClose }: ChinaDialogProps) {
  const ready = status?.status === "ready" ? status : null;
  return (
    <div className="pc-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className="pc-dialog pc-china"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-china-title"
        lang="zh-Hans"
        onKeyDown={(event) => event.key === "Escape" && onClose()}
      >
        <h2 id="pc-china-title" className="pc-dialog-title">
          使用国内服务器激活
        </h2>
        <p className="pc-dialog-text">在中国大陆，Typora 可能无法连接默认的激活服务器，建议使用国内服务器激活。</p>
        {ready ? (
          <p className="pc-dialog-text">点击下方按钮会打开 Typora 2，切换到国内服务器并自动激活。</p>
        ) : (
          <p className="pc-dialog-text">
            订单处理中，许可证生成后会显示在本页，并发送到你的邮箱（可能需要几分钟，如未收到请检查垃圾邮件）。手动激活前，请先在 Typora 的「设置 › 通用」中开启「Typora 服务使用国内服务器」。
          </p>
        )}
        <div className="pc-dialog-actions">
          <button type="button" className="pc-text-btn" onClick={onClose} autoFocus={!ready}>
            {ready ? "稍后" : "知道了"}
          </button>
          {ready ? (
            <a className="submit-btn" href={activationLink(ready.email, ready.licenses[0]!, true)} onClick={onClose} autoFocus>
              使用国内服务器激活
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// MARK: - Order result

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="pc-copy"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        });
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

const SHOP_COPY: LetterCopy = {
  title: "Purchase",
  body1:
    "Typora is a one-time purchase, not a subscription. A license is yours to use on up to three devices, and it doesn't expire.",
  body2Html: CHINA_MIRROR
    ? `One license is <strong>${PRICE}</strong>, paid with Alipay. Your license code is emailed to you once the order is complete.`
    : `One license is <strong>${PRICE}</strong>. Check out below, and activate Typora right from this page once your license is ready.`,
  closing: "Prices exclude tax. If Typora isn't for you, you can get a full refund within 30 days.",
};

/** What the letter says, and what sits under it, for a paid order. */
function orderCopy(status: OrderStatus | null, processing: boolean): LetterCopy {
  switch (status?.status) {
    case "ready":
      return {
        title: "Thank You",
        body1: "Your purchase is complete. Thank you for supporting Typora.",
        body2Html: `Your license is ready below, and it's also sent to <strong>${escapeHtml(status.email)}</strong>. <strong>Activate my Typora</strong> opens Typora 2 and activates it on this device.`,
        closing: "Keep the email: it has your receipt, and the code works on up to three devices.",
      };
    case "unverified":
      return {
        title: "On Its Way",
        body1: "Payment received. We couldn't look the order up from this page, but your license is on its way.",
        body2Html: "It's sent to the email address you entered at checkout. It can take a while, so check your spam or junk folder too.",
        closing: "Still nothing after an hour? Write to hi@typora.io with your order receipt.",
      };
    case "disabled":
      return {
        title: "No Active License",
        body1: "This order's license was disabled, for example after a refund.",
        body2Html: "If that isn't right, please write to <strong>hi@typora.io</strong> with your receipt.",
        closing: "",
      };
    default:
      return processing
        ? {
            title: "Processing Your Order",
            body1: "Payment received. Processing the order can take a while.",
            body2Html:
              "Payment received. Your license will appear below once it's ready, and it's also sent to the email address you entered at checkout.",
            closing: "Orders that need a closer look by the payment provider can take longer.",
          }
        : {
            title: "Almost There",
            body1: "Payment received. We're getting your license ready.",
            body2Html: "Payment received. This usually takes a few seconds; keep this page open, and your license appears below.",
            closing: "",
          };
  }
}

type OrderActionProps = {
  order: Order;
  status: OrderStatus | null;
  processing: boolean;
  failed: boolean;
  onChinaServer(): void;
  onNewOrder(): void;
};

/** Under the letter of a paid order, where the wait-list form sits. */
function OrderAction({ order, status, processing, failed, onChinaServer, onNewOrder }: OrderActionProps) {
  const chinaServer =
    order.country === "CN" ? (
      <button type="button" className="pc-text-btn" onClick={onChinaServer} lang="zh-Hans">
        使用国内服务器激活
      </button>
    ) : null;
  if (status?.status === "ready") {
    const [first] = status.licenses;
    return (
      <div aria-live="polite">
        <span className="waitlist-label">{status.licenses.length > 1 ? "Your licenses" : "Your license"}</span>
        <dl className="pc-license">
          <dt>Email</dt>
          <dd>
            <code>{status.email}</code>
            <CopyButton text={status.email} />
          </dd>
          <dt>{status.licenses.length > 1 ? "License codes" : "License code"}</dt>
          {status.licenses.map((license) => (
            <dd key={license}>
              <code>{license}</code>
              <CopyButton text={license} />
            </dd>
          ))}
        </dl>
        <div className="pc-actions">
          <a className="submit-btn pc-button" data-tag-target href={activationLink(status.email, first!, false)}>
            Activate my Typora
          </a>
          {chinaServer}
        </div>
        <p className="pc-hint">
          If nothing happens, open Typora, choose <strong>Help › My License</strong>, and enter the email and code
          above.
        </p>
      </div>
    );
  }
  if (status?.status === "disabled") {
    return (
      <div className="pc-actions">
        <button type="button" className="submit-btn pc-button" data-tag-target onClick={onNewOrder}>
          Buy a license
        </button>
      </div>
    );
  }
  if (status?.status === "unverified") {
    return (
      <p className="pc-status" role="status">
        Check your inbox for an email with your license code.
      </p>
    );
  }
  return (
    <div aria-live="polite">
      <span className="waitlist-label">Your license</span>
      <p className="pc-status" role="status">
        <span className="pc-spinner" aria-hidden="true" />
        {failed ? "Can't reach the store right now. Still trying…" : "Waiting for your license…"}
      </p>
      {processing ? (
        <p className="pc-hint">
          No email within an hour? Check your spam or junk folder, then write to{" "}
          <a href="mailto:hi@typora.io">hi@typora.io</a> with your order receipt.
        </p>
      ) : null}
      {chinaServer ? <div className="pc-actions">{chinaServer}</div> : null}
    </div>
  );
}

type ShopActionProps = {
  quantity: number;
  setQuantity(update: (count: number) => number): void;
  troubled: boolean;
  onPurchase(alternative: boolean): void;
};

/** The purchase controls, where the wait-list form sits. */
function ShopAction({ quantity, setQuantity, troubled, onPurchase }: ShopActionProps) {
  return (
    <>
      <span className="waitlist-label">Get a license</span>
      <div className="form-row">
        <div className="pc-quantity" role="group" aria-label="Number of licenses">
          <button
            type="button"
            aria-label="One fewer license"
            disabled={quantity <= 1}
            onClick={() => setQuantity((count) => Math.max(1, count - 1))}
          >
            −
          </button>
          <span className="pc-quantity-value" aria-live="polite">
            {quantity} {quantity === 1 ? "license" : "licenses"}
          </span>
          <button
            type="button"
            aria-label="One more license"
            disabled={quantity >= 99}
            onClick={() => setQuantity((count) => Math.min(99, count + 1))}
          >
            +
          </button>
        </div>
        {CHINA_MIRROR ? (
          <a className="submit-btn pc-button" data-tag-target href={alipayCheckoutUrl(quantity)}>
            Purchase
          </a>
        ) : (
          <button type="button" className="submit-btn pc-button" data-tag-target onClick={() => onPurchase(false)}>
            Purchase
          </button>
        )}
      </div>
      {CHINA_MIRROR ? (
        <p className="pc-hint" lang="zh-Hans">
          订单完成后，许可证会发送到你的邮箱（可能有数分钟延时）。如无法激活，请在 Typora「设置 › 通用」中开启「Typora
          服务使用国内服务器」后再试。
        </p>
      ) : troubled ? (
        <p className="pc-hint">
          Having trouble paying?{" "}
          <button type="button" className="pc-link" onClick={() => onPurchase(true)}>
            Try another payment method
          </button>
        </p>
      ) : null}
    </>
  );
}

// MARK: - Page

export function PurchasePage() {
  const [quantity, setQuantity] = useState(() => {
    const count = Number(new URLSearchParams(window.location.search).get("orderCount"));
    return Number.isInteger(count) && count > 0 && count < 100 ? count : 1;
  });
  const [country, setCountry] = useState(guessCountry);
  const [picking, setPicking] = useState(false);
  const [opening, setOpening] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  /** After a cancelled or failed checkout: offer the other provider. */
  const [troubled, setTroubled] = useState(false);
  const [alternative, setAlternative] = useState(false);
  const [order, setOrder] = useState<Order | null>(() => RETURNED_ORDER ?? readOrder());
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [processing, setProcessing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [chinaDialog, setChinaDialog] = useState(RETURNED_ORDER?.country === "CN");

  // Back from 2Checkout: keep the order, and drop its parameters from the URL.
  useEffect(() => {
    if (!RETURNED_ORDER) return;
    storeOrder(RETURNED_ORDER);
    window.history.replaceState(null, "", withoutReturnParams(window.location.href));
  }, []);

  useEffect(() => {
    document.title = "Purchase Typora";
    if (!order && !CHINA_MIRROR) preloadCheckouts();
  }, [order]);

  // FastSpring: the popup forwards the licenses, if they exist yet. Until
  // then (an order in review, say) the email brings them.
  useEffect(() => {
    if (order?.payment.provider !== "fastspring") return;
    const { id } = order.payment;
    if (order.ready) {
      setStatus({ status: "ready", ...order.ready });
      return;
    }
    setStatus({ status: "pending" });
    const age = Date.now() - order.paidAt;
    setProcessing(age >= PROCESSING_AFTER);
    const timer = window.setTimeout(() => setProcessing(true), Math.max(0, PROCESSING_AFTER - age));
    const stop = onFastSpringOrder((received) => {
      if (received.id !== id) return;
      const next = { ...order, ready: { email: received.email, licenses: received.licenses } };
      storeOrder(next);
      setOrder(next);
    });
    return () => {
      stop();
      window.clearTimeout(timer);
    };
  }, [order]);

  // 2Checkout: poll the store until the order's licenses exist, every few
  // seconds at first, then less often while the order is processed.
  useEffect(() => {
    if (order?.payment.provider !== "2checkout") return;
    const proof = order.payment;
    let stopped = false;
    let timer = 0;
    const controller = new AbortController();
    setStatus(null);
    setFailed(false);
    const poll = async () => {
      let next: OrderStatus | null = null;
      try {
        next = await fetchOrderStatus(proof, controller.signal);
        setFailed(false);
      } catch {
        if (stopped) return;
        setFailed(true);
      }
      if (stopped) return;
      if (next) setStatus(next);
      if (next && next.status !== "pending") return;
      const age = Date.now() - order.paidAt;
      setProcessing(age >= PROCESSING_AFTER);
      timer = window.setTimeout(poll, age < 2 * 60_000 ? 3_000 : age < 30 * 60_000 ? 15_000 : 60_000);
    };
    void poll();
    return () => {
      stopped = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [order]);

  const startCheckout = async (picked: string) => {
    setCountry(picked);
    setOpening(true);
    setCheckoutError("");
    try {
      sessionStorage.setItem(CHECKOUT_COUNTRY_KEY, picked);
    } catch {
      /* the China dialog won't know after 2Checkout's redirect */
    }
    try {
      await openCheckout(
        { provider: providerFor(picked, alternative), country: picked, quantity },
        {
          onPaid: (id) => {
            const paid: Order = { payment: { provider: "fastspring", id }, country: picked, paidAt: Date.now() };
            storeOrder(paid);
            setOrder(paid);
            setChinaDialog(picked === "CN");
          },
          onCancelled: () => setTroubled(true),
        },
      );
      setPicking(false);
    } catch {
      setTroubled(true);
      setCheckoutError(
        "The checkout couldn't load. Turn off ad blockers, VPNs or proxies for this page and try again, or try another payment method.",
      );
    } finally {
      setOpening(false);
    }
  };

  const newOrder = () => {
    storeOrder(null);
    setOrder(null);
    setStatus(null);
    setProcessing(false);
    setChinaDialog(false);
  };

  const copy = order ? orderCopy(status, processing) : SHOP_COPY;
  const slot: PurchaseSlot = {
    copy,
    layoutKey: order ? `${status?.status ?? "none"}:${processing}:${failed}` : `shop:${troubled}`,
    action: order ? (
      <OrderAction
        order={order}
        status={status}
        processing={processing}
        failed={failed}
        onChinaServer={() => setChinaDialog(true)}
        onNewOrder={newOrder}
      />
    ) : (
      <ShopAction
        quantity={quantity}
        setQuantity={setQuantity}
        troubled={troubled}
        onPurchase={(alternative) => {
          setAlternative(alternative);
          setCheckoutError("");
          setPicking(true);
        }}
      />
    ),
  };

  return (
    <>
      <App purchase={slot} />
      {picking && !order ? (
        <CountryDialog
          initial={country}
          busy={opening}
          error={checkoutError}
          onCancel={() => setPicking(false)}
          onContinue={(picked) => void startCheckout(picked)}
        />
      ) : null}
      {chinaDialog && order ? <ChinaServerDialog status={status} onClose={() => setChinaDialog(false)} /> : null}
    </>
  );
}
